import { useState, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Eye, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface KanbanItem {
  id: string;
  status: string;
  [key: string]: unknown;
}

interface KanbanBoardProps<T extends KanbanItem> {
  items: T[];
  statuses: string[];
  statusLabels: Record<string, string>;
  onItemClick: (item: T) => void;
  onStatusChange: (itemId: string, newStatus: string) => void;
  renderItem: (item: T) => React.ReactNode;
  emptyMessage?: string;
  /** Optional per-column sort. Returns the items as they should appear in
   * the given column. If omitted, items render in their input order. */
  sortColumnItems?: (items: T[], status: string) => T[];
  /** Optional UI rendered in each column header (next to the count chip) —
   * used for per-column sort controls. */
  renderColumnHeaderExtra?: (status: string) => React.ReactNode;
}

export function KanbanBoard<T extends KanbanItem>({
  items,
  statuses,
  statusLabels,
  onItemClick,
  onStatusChange,
  renderItem,
  emptyMessage = 'No items',
  sortColumnItems,
  renderColumnHeaderExtra,
}: KanbanBoardProps<T>) {
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null);
  const [hiddenStatuses, setHiddenStatuses] = useState<Set<string>>(new Set());
  const [showVisibilityMenu, setShowVisibilityMenu] = useState(false);

  const visibleStatuses = useMemo(() => {
    return statuses.filter((status) => !hiddenStatuses.has(status));
  }, [statuses, hiddenStatuses]);

  const toggleStatusVisibility = (status: string) => {
    setHiddenStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  };

  const handleDragStart = (e: React.DragEvent, itemId: string) => {
    setDraggedItemId(itemId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', itemId);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetStatus: string) => {
    e.preventDefault();
    if (draggedItemId) {
      const item = items.find((i) => i.id === draggedItemId);
      if (item && item.status !== targetStatus) {
        onStatusChange(draggedItemId, targetStatus);
      }
    }
    setDraggedItemId(null);
  };

  const handleDragEnd = () => {
    setDraggedItemId(null);
  };

  const itemsByStatus = items.reduce((acc, item) => {
    if (!acc[item.status]) {
      acc[item.status] = [];
    }
    acc[item.status].push(item);
    return acc;
  }, {} as Record<string, T[]>);

  return (
    <div className="w-full">
      {/* Status visibility controls - sticky, constrained to viewport width */}
      <div className="sticky top-[73px] z-10 bg-background pb-2 flex justify-end border-b mb-4">
        <div className="relative">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowVisibilityMenu(!showVisibilityMenu)}
          >
            <Eye className="h-4 w-4 mr-2" />
            Columns
            <ChevronDown className={cn('h-4 w-4 ml-2 transition-transform', showVisibilityMenu && 'rotate-180')} />
          </Button>
          {showVisibilityMenu && (
            <>
              <div 
                className="fixed inset-0 z-30" 
                onClick={() => setShowVisibilityMenu(false)}
              />
              <div className="absolute right-0 mt-2 z-40 w-48 rounded-md border bg-background shadow-lg">
                <div className="p-2">
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Show columns
                  </div>
                  {statuses.map((status) => {
                    const isHidden = hiddenStatuses.has(status);
                    return (
                      <label
                        key={status}
                        className="flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-accent cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={!isHidden}
                          onChange={() => toggleStatusVisibility(status)}
                          className="h-4 w-4 rounded border-gray-300"
                        />
                        <span className="text-sm">{statusLabels[status] || status}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Kanban columns - scrollable container, constrained to viewport width */}
      <div className="w-full overflow-x-auto">
        <div className="flex gap-4 pb-4" style={{ minWidth: 'max-content' }}>
        {visibleStatuses.map((status) => {
          const rawItems = itemsByStatus[status] || [];
          const statusItems = sortColumnItems ? sortColumnItems(rawItems, status) : rawItems;
          return (
            <div
              key={status}
              className="flex-shrink-0 w-80"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, status)}
            >
              <div className="mb-2 flex items-center justify-between gap-2">
                <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">
                  {statusLabels[status] || status}
                </h3>
                <div className="flex items-center gap-1">
                  {renderColumnHeaderExtra?.(status)}
                  <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                    {statusItems.length}
                  </span>
                </div>
              </div>
              <div className="space-y-2 min-h-[400px] rounded-lg border-2 border-dashed border-muted p-2">
                {statusItems.map((item) => (
                  <Card
                    key={item.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, item.id)}
                    onDragEnd={handleDragEnd}
                    onClick={() => onItemClick(item)}
                    className={cn(
                      'cursor-move hover:border-primary/50 transition-colors',
                      draggedItemId === item.id && 'opacity-50'
                    )}
                  >
                    <CardContent className="p-3">
                      {renderItem(item)}
                    </CardContent>
                  </Card>
                ))}
                {statusItems.length === 0 && (
                  <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
                    {emptyMessage}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        </div>
      </div>
    </div>
  );
}
